import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import Disclaimer from "@/components/Disclaimer";
import { AtoDisclaimer } from "@/components/tax/AtoDisclaimer";
import { computeGstFromInclusive, getGstStamp } from "@/tax/gst";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

import AttachmentsPanel, { uploadDraftAttachments } from "@/components/AttachmentsPanel";
import AISummaryCard from "@/components/review/AISummaryCard";
import TwinInsightsCard from "@/components/review/TwinInsightsCard";
import DocketPreviewCard from "@/components/review/DocketPreviewCard";
import EditableFieldsCard from "@/components/review/EditableFieldsCard";
import PersonalExpenseToggle from "@/components/review/PersonalExpenseToggle";
import { WFH_RATE_PER_HOUR } from "@/components/personal-tax/constants";
import SaveActions from "@/components/review/SaveActions";

export default function Review() {
  const nav = useNavigate();
  const location = useLocation();
  const { jobs, activeJobId } = useApp();
  const parsed = location.state?.parsed || {};
  const source = location.state?.source || parsed.source || "manual";
  const supplierSuggestions = location.state?.supplierSuggestions || [];

  const [form, setForm] = useState({
    supplier: parsed.supplier || "",
    description: parsed.description || "",
    total: parsed.total || 0,
    gst: parsed.gst || 0,
    gst_locked: false,
    entry_date: parsed.entry_date || new Date().toISOString().slice(0, 10),
    deduction_category: parsed.deduction_category || "materials",
    category: parsed.category || "",
    cost_code: parsed.cost_code || "",
    notes: parsed.notes || "",
    job_id: parsed.job_id || activeJobId || "",
    photo_path: parsed.photo_path || null,
    voice_path: parsed.voice_path || null,
    attachments: parsed.attachments || [],
    raw_transcript: parsed.raw_transcript || "",
    source,
    confidence: parsed.confidence || {},
    status: "pending",
  });
  const [twin, setTwin] = useState(parsed.twin || null);
  const [savedEntryId, setSavedEntryId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [jobTouched, setJobTouched] = useState(false);
  const [personalPayload, setPersonalPayload] = useState({ on: false, type: null, sub_category: null, property_id: null, hours: null });
  // Attachments-on-draft: File objects the tradie picks BEFORE the entry
  // exists in the DB. Uploaded after `POST /entries` returns. Kept if the
  // upload later fails so the tradie doesn't have to re-pick from the
  // OS gallery on the next Save tap.
  const [draftFiles, setDraftFiles] = useState([]);

  // Twin: re-query on supplier change (debounced)
  useEffect(() => {
    const supplier = String(form.supplier || "").trim();
    if (!supplier) {
      setTwin(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/suppliers/suggest", { supplier });
        if (!data.twin) {
          setTwin(null);
          return;
        }
        setTwin({ ...data.twin, filled: [] });
        setForm((f) => {
          const filled = [];
          const next = { ...f };
          for (const k of ["deduction_category", "category", "cost_code"]) {
            const current = String(f[k] || "").trim();
            const isDefault =
              k === "deduction_category" && current === "materials" && source !== "manual";
            if ((!current || isDefault) && data.twin[k]) {
              next[k] = data.twin[k];
              filled.push(k);
            }
          }
          if (filled.length) setTwin((prev) => (prev ? { ...prev, filled } : prev));
          return next;
        });
      } catch (err) {
        devLog.warn("supplier suggest failed", err);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.supplier, source]);

  // Auto GST when total changes and GST is unlocked. Rate comes from the
  // 2026.1 ATO Knowledge Base via `computeGstFromInclusive` (falls back to
  // 10% historical rate if the KB read ever returns undefined).
  useEffect(() => {
    if (form.gst_locked) return;
    const t = Number(form.total) || 0;
    const g = computeGstFromInclusive(t);
    setForm((f) => ({ ...f, gst: g }));
  }, [form.total, form.gst_locked]);

  const netAmount = useMemo(() => {
    const t = Number(form.total) || 0;
    const g = Number(form.gst) || 0;
    return Math.max(0, t - g);
  }, [form.total, form.gst]);

  const filledCount = useMemo(() => {
    const fields = ["supplier", "total", "description", "deduction_category", "job_id"];
    return fields.filter((k) => {
      const v = form[k];
      return (v !== "" && v !== null && Number(v) !== 0) || (k !== "total" && !!v);
    }).length;
  }, [form]);

  const undoTwin = useCallback(() => {
    if (!twin?.filled?.length) return;
    setForm((f) => {
      const next = { ...f };
      for (const k of twin.filled) {
        next[k] = k === "deduction_category" ? "materials" : "";
      }
      return next;
    });
    setTwin((t) => (t ? { ...t, filled: [] } : t));
  }, [twin]);

  const applyJob = useCallback(
    (jobId, opts = {}) => {
      setForm((f) => ({ ...f, job_id: jobId || "" }));
      if (!opts.auto) setJobTouched(true);
      if (opts.auto && opts.suggestion) {
        toast.success(
          `AI picked "${opts.suggestion.job_name}" (${opts.suggestion.confidence}%) — tap to change.`,
          { duration: 3500 }
        );
      }
    },
    []
  );

  const save = async (status = "pending") => {
    const supplierEmpty = !String(form.supplier || "").trim();
    const totalZero = !(Number(form.total) > 0);
    if (supplierEmpty && totalZero) {
      toast.error("Add at least a supplier or a total to save");
      return;
    }

    // Personal-tax fast-path: on Approve, ship the parsed docket straight
    // into personal_entries and skip the business ledger entirely. Pending
    // saves still go to the business queue so the user can attach files first.
    if (personalPayload.on && status === "approved") {
      if (!personalPayload.type) {
        toast.error("Pick a personal-tax kind before approving");
        return;
      }
      if (personalPayload.type.startsWith("rental") && !personalPayload.property_id) {
        toast.error("Add a rental property in Personal Tax first, then approve.");
        return;
      }
      setBusy(true);
      try {
        const isWfh = personalPayload.sub_category === "wfh_hours";
        const amount = isWfh
          ? (Number(personalPayload.hours) || 0) * WFH_RATE_PER_HOUR
          : Number(form.total) || 0;
        if (!amount) {
          toast.error(isWfh ? "Enter hours worked from home" : "Total is required");
          setBusy(false);
          return;
        }
        // Prefer an already-uploaded attachment path; else the primary photo path.
        const firstAttach = Array.isArray(form.attachments) && form.attachments.length > 0
          ? (form.attachments[0].url || form.attachments[0].path)
          : null;
        await api.post("/personal-tax/entries", {
          type: personalPayload.type,
          sub_category: personalPayload.sub_category,
          amount,
          gst: Number(form.gst) || 0,
          date: form.entry_date,
          notes: form.description || form.notes || "",
          property_id: personalPayload.property_id,
          payer: form.supplier || null,
          hours: isWfh ? Number(personalPayload.hours) || null : null,
          attachment_url: firstAttach || form.photo_path || null,
          source: source === "manual" ? "manual" : "docket",
        });
        // If a pending business entry was created earlier (savedEntryId), delete it so
        // the ledger doesn't hold a duplicate copy.
        if (savedEntryId) {
          try {
            await api.delete(`/entries/${savedEntryId}`);
          } catch (err) {
            devLog.debug("pending business entry cleanup on personal save failed (soft)", err);
          }
        }
        toast.success("Saved to Personal Tax");
        nav("/personal-tax");
        return;
      } catch (err) {
        devLog.warn("personal save failed", err);
        toast.error(err?.response?.data?.detail || "Could not save personal entry");
        return;
      } finally {
        setBusy(false);
      }
    }

    setBusy(true);
    try {
      if (savedEntryId) {
        await api.put(`/entries/${savedEntryId}`, { ...form, status });
        // Flush any drafts the tradie added since the last save.
        if (draftFiles.length) {
          try {
            const added = await uploadDraftAttachments("entry", savedEntryId, draftFiles);
            setForm((f) => ({ ...f, attachments: [...(f.attachments || []), ...added] }));
            setDraftFiles([]);
          } catch (e) {
            devLog.warn("draft flush on update failed", e);
            toast("Photos will upload next time you save");
          }
        }
        toast.success(status === "approved" ? "Approved & saved to ledger" : "Updated in queue");
        if (status === "approved") nav("/history");
        return;
      }
      // ATO ruleset audit stamp — persisted with the entry so a future
      // audit can prove which ruleset produced the GST portion.
      const stamp = getGstStamp();
      const { data } = await api.post("/entries", {
        ...form,
        status,
        ato_ruleset_version: stamp.version,
        ato_ruleset_stamped_at: stamp.stampedAt,
      });
      setSavedEntryId(data.id);
      // Keep the console log as a dev breadcrumb in addition to persistence.
      devLog.info("Review save ATO stamp", stamp, { entryId: data.id });

      // Attach any draft photos/PDFs the tradie added BEFORE Save. Kept
      // on failure so the tradie doesn't re-pick from the OS gallery.
      let attachedNow = [];
      if (draftFiles.length) {
        try {
          attachedNow = await uploadDraftAttachments("entry", data.id, draftFiles);
          setForm((f) => ({ ...f, attachments: [...(f.attachments || []), ...attachedNow] }));
          setDraftFiles([]);
        } catch (e) {
          devLog.warn("draft attachments upload failed after entry create", e);
          toast("Entry saved — photos will upload next time you save");
        }
      }

      if (status === "approved") {
        toast.success("Approved & saved to ledger");
        nav("/history");
      } else {
        const attMsg = attachedNow.length ? ` with ${attachedNow.length} attachment${attachedNow.length === 1 ? "" : "s"}` : "";
        toast.success(`Saved${attMsg} — you can add more below`);
      }
    } catch (err) {
      devLog.warn("save failed", err);
      toast.error("Could not save entry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => nav(-1)}
          className="w-11 h-11 rounded-xl bi-ghost-btn flex items-center justify-center"
          data-testid="review-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-extrabold leading-tight">Review before save</h1>
          <div className="text-xs text-white/50 mt-0.5">
            Check the fields — AI sometimes gets creative.
          </div>
        </div>
      </div>

      <AISummaryCard
        source={source}
        form={form}
        twin={twin}
        filledCount={filledCount}
        netAmount={netAmount}
      />

      {twin && (twin.deduction_category || twin.category || twin.cost_code || twin.avg_total > 0) && (
        <div className="bi-rise bi-rise-1">
          <TwinInsightsCard twin={twin} form={form} onUndo={undoTwin} />
        </div>
      )}

      <div className="bi-rise bi-rise-2">
        <DocketPreviewCard
          photoPath={form.photo_path}
          voicePath={form.voice_path}
          transcript={form.raw_transcript}
        />
      </div>

      <div className="bi-rise bi-rise-3">
        <AttachmentsPanel
          entryId={savedEntryId}
          attachments={form.attachments || []}
          onChange={(next) => setForm((f) => ({ ...f, attachments: next }))}
          draftFiles={draftFiles}
          onDraftFilesChange={setDraftFiles}
          onOcrApplied={(parsedOcr, filled) => {
            if (!filled?.length) return;
            setForm((f) => {
              const next = { ...f };
              const isEmpty = (v) => v === null || v === undefined || v === "" || v === 0;
              for (const k of filled) {
                if (isEmpty(next[k]) && !isEmpty(parsedOcr[k])) {
                  next[k] = parsedOcr[k];
                }
              }
              return next;
            });
          }}
        />
      </div>

      <div className="bi-rise bi-rise-4">
        <EditableFieldsCard
          form={form}
          setForm={setForm}
          jobs={jobs}
          activeJobId={activeJobId}
          supplierSuggestions={supplierSuggestions}
          jobTouched={jobTouched}
          onApplyJob={applyJob}
        />
      </div>

      <div className="bi-rise bi-rise-5">
        <PersonalExpenseToggle
          suggestion={parsed.personal_suggestion}
          initialOn={Boolean(parsed.personal_suggestion)}
          onChange={setPersonalPayload}
        />
      </div>

      <AtoDisclaimer context="capture" testId="review-disclaimer" />
      {/* Legacy plain Disclaimer kept as a hidden sr-only fallback for older
          Playwright regressions that queried its exact text — will be removed
          the next time we touch Review. */}
      <div className="sr-only">
        <Disclaimer text="Field values were suggested by AI. Please double-check the total and GST before saving. Not official accounting advice." />
      </div>

      <SaveActions
        busy={busy}
        savedEntryId={savedEntryId}
        onSavePending={() => save("pending")}
        onApprove={() => save("approved")}
      />

      {savedEntryId && (
        <button
          type="button"
          onClick={() => nav("/approval")}
          data-testid="rv-continue-queue"
          className="w-full h-11 text-sm text-white/70 hover:text-white text-center"
        >
          Continue to Approval Queue →
        </button>
      )}
    </div>
  );
}
